import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import "./Background.css";
import smokeSvg from "./assets/smokeSvg";

function Background() {
  const mountRef = useRef();

  useEffect(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;

    // ======= CONFIG =======
    const TRACKING_THRESHOLD_PERCENT = 30; // Cursor must cross 35% of screen width
    const TRACKING_THRESHOLD = TRACKING_THRESHOLD_PERCENT / 100; // Normalized (0–1)
    const STARTING_TOP_PERCENTAGE = 3; // Start at 3% from top
    const INITIAL_TARGET_Y = 1 - STARTING_TOP_PERCENTAGE / 100;
    const STARTING_X_PERCENTAGE = 80; // Start at 80% from left
    const INITIAL_TARGET_X = STARTING_X_PERCENTAGE / 100;

    const BACKGROUND_MOVEMENT_SPEED = 0.12; // How quickly background follows target
    const CURSOR_INFLUENCE_SPEED = 0.3; // How strongly cursor distorts smoke
    // =======================

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(30, width / height, 0.1, 1000);
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    mountRef.current.appendChild(renderer.domElement);

    // Modify SVG aspect ratio
    let svgString = smokeSvg;
    svgString = svgString.replace(
      'preserveAspectRatio="xMidYMid slice"',
      'preserveAspectRatio="xMidYMin slice"'
    );

    const blob = new Blob([svgString], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);

    const textureLoader = new THREE.TextureLoader();

    let targetX = INITIAL_TARGET_X;
    let targetY = INITIAL_TARGET_Y;
    let currentX = INITIAL_TARGET_X;
    let currentY = INITIAL_TARGET_Y;

    textureLoader.load(url, (texture) => {
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.needsUpdate = true;

      const smokeInstances = [];
      const instanceCount = 6;

      const fov = THREE.MathUtils.degToRad(45);
      const planeHeight = 11;
      const cameraZ = 5;
      const viewHeight = 2 * Math.tan(fov / 2) * cameraZ;
      const viewWidth = viewHeight * (width / height);

      const initialTopPosition = viewHeight * (INITIAL_TARGET_Y - 0.5);
      const initialPlaneTopAtPosition = initialTopPosition - planeHeight / 2;
      const initialPlaneXPosition = (INITIAL_TARGET_X - 0.5) * viewWidth;

      for (let i = 0; i < instanceCount; i++) {
        const geometry = new THREE.PlaneGeometry(8, planeHeight);
        const material = new THREE.ShaderMaterial({
          uniforms: {
            uTime: { value: 0 },
            uTexture: { value: texture },
            uMouse: {
              value: new THREE.Vector2(INITIAL_TARGET_X, INITIAL_TARGET_Y),
            },
            uOpacity: { value: 1.0 - i * 0.15 },
            uOffset: { value: i * 0.08 },
            uCursorSpeed: { value: CURSOR_INFLUENCE_SPEED }, // NEW
          },
          vertexShader: `
            varying vec2 vUv;
            void main() {
              vUv = uv;
              vec3 pos = position;
              gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
            }
          `,
          fragmentShader: `
            uniform sampler2D uTexture;
            uniform float uTime;
            uniform vec2 uMouse;
            uniform float uOpacity;
            uniform float uOffset;
            uniform float uCursorSpeed;
            varying vec2 vUv;

            void main() {
              vec2 uv = vUv;

              float heightFactor = smoothstep(0.0, 1.0, vUv.y);
              float wave1 = 0.02 * sin(10.0 * uv.y + uTime + uOffset) * heightFactor;
              float wave2 = 0.02 * cos(10.0 * uv.x + uTime + uOffset) * heightFactor;
              uv.x += wave1;
              uv.y += wave2;

              float mouseInfluence = pow(vUv.y, 2.0);
              vec2 diff = uv - uMouse;
              float dist = length(diff);
              float mouseStrength = uCursorSpeed * uOpacity * mouseInfluence; // UPDATED
              float falloff = exp(-dist * 2.0);

              uv += normalize(diff) * mouseStrength * falloff;

              float stretchFactor = mouseInfluence * 0.2 * (1.0 + 0.1 * sin(uTime + uOffset));
              vec2 stretch = (uMouse - uv) * stretchFactor;
              uv += stretch;

              vec4 color = texture2D(uTexture, uv);
              color.a *= uOpacity;

              gl_FragColor = color;
            }
          `,
          transparent: true,
          blending: THREE.NormalBlending,
        });

        const plane = new THREE.Mesh(geometry, material);
        plane.position.x = initialPlaneXPosition;
        plane.position.y = initialPlaneTopAtPosition;
        plane.position.z = -i * 0.02;
        scene.add(plane);

        smokeInstances.push({
          mesh: plane,
          material: material,
          delay: i * 0.1,
          baseX: initialPlaneXPosition,
          baseY: initialPlaneTopAtPosition,
        });
      }

      const animate = (time) => {
        requestAnimationFrame(animate);

        // Use BACKGROUND_MOVEMENT_SPEED
        currentX += (targetX - currentX) * BACKGROUND_MOVEMENT_SPEED;
        currentY += (Math.max(targetY, 0.2) - currentY) * BACKGROUND_MOVEMENT_SPEED;

        smokeInstances.forEach((instance, index) => {
          const delayFactor = 1.0 - index * 0.15;
          const delayedX = currentX;
          const delayedY = currentY;

          instance.material.uniforms.uTime.value = time * 0.001;
          instance.material.uniforms.uMouse.value.set(delayedX, delayedY);

          const screenX = (delayedX - 0.5) * viewWidth;
          const screenY = (delayedY - 0.5) * viewHeight;

          const meshLerpFactor = BACKGROUND_MOVEMENT_SPEED * delayFactor * 0.7;
          instance.mesh.position.x += (screenX - instance.mesh.position.x) * meshLerpFactor;
          instance.mesh.position.y += (screenY - (instance.mesh.position.y + planeHeight / 2)) * meshLerpFactor;
        });

        renderer.render(scene, camera);
      };
      animate(0);

      // === Mouse Events with Threshold ===
      const handleMouseMove = (e) => {
        const screenX = e.clientX / window.innerWidth;

        if (screenX > TRACKING_THRESHOLD) {
          targetX = screenX;
          targetY = Math.max(1.0 - e.clientY / window.innerHeight, 0.2);
        } else {
          targetX = INITIAL_TARGET_X;
          targetY = INITIAL_TARGET_Y;
        }
      };

      const handleMouseLeave = () => {
        targetX = INITIAL_TARGET_X;
        targetY = INITIAL_TARGET_Y;
      };

      const handleMouseEnter = (e) => {
        const screenX = e.clientX / window.innerWidth;
        if (screenX > TRACKING_THRESHOLD) {
          targetX = screenX;
          targetY = Math.max(1.0 - e.clientY / window.innerHeight, 0.2);
        }
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseleave", handleMouseLeave);
      window.addEventListener("mouseenter", handleMouseEnter);

      const handleResize = () => {
        const newWidth = window.innerWidth;
        const newHeight = window.innerHeight;

        camera.aspect = newWidth / newHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(newWidth, newHeight);
      };

      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseleave", handleMouseLeave);
        window.removeEventListener("mouseenter", handleMouseEnter);
        window.removeEventListener("resize", handleResize);

        if (mountRef.current && mountRef.current.contains(renderer.domElement)) {
          mountRef.current.removeChild(renderer.domElement);
        }

        smokeInstances.forEach((instance) => {
          instance.material.dispose();
          instance.mesh.geometry.dispose();
        });
        texture.dispose();
        renderer.dispose();
        URL.revokeObjectURL(url);
      };
    });
  }, []);

  return (
    <div
      className="background-canvas"
      ref={mountRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100vh",
        zIndex: -1,
        pointerEvents: "none",
      }}
    />
  );
}

export default Background;